import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Briefcase, Loader2, X } from 'lucide-react';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const PAGE = 40;

/** Jobs the partner app / RSA / WhatsApp / B2B can complete. Not call-center history. */
export const PARTNER_APP_JOB_SOURCES = [
  'customer_rsa_app',
  'customer_rsa_bargain',
  'customer_rsa_on_demand',
  'enterprise_manual',
  'enterprise_api',
  'whatsapp_rsa',
  'whatsapp_ai',
];

const JOB_SELECT =
  'id, service_type, source, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, assigned_mechanic_id, assigned_mechanic_name, customer_name, vehicle_number, problem, requested_at, complaint_registered_at, updated_at';

const parseLatLng = (lat, lng) => {
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return [a, b];
};

const coordsFromMapsUrl = (url) => {
  const text = String(url || '');
  const q = text.match(/[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i);
  if (q) return parseLatLng(q[1], q[2]);
  const at = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) return parseLatLng(at[1], at[2]);
  const pair = text.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (pair) return parseLatLng(pair[1], pair[2]);
  return null;
};

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const a = parseLatLng(lat1, lng1);
  const b = parseLatLng(lat2, lng2);
  if (!a || !b) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)) * 10) / 10;
};

const jobPickupCoords = (job) =>
  parseLatLng(job?.pickup_latitude, job?.pickup_longitude) ||
  coordsFromMapsUrl(job?.location_link) ||
  coordsFromMapsUrl(job?.pickup_address);

const distanceKm = (partner, job) => {
  const pickup = jobPickupCoords(job);
  if (!pickup || !partner) return null;
  const km = haversineKm(
    partner.service_center_lat,
    partner.service_center_lng,
    pickup[0],
    pickup[1],
  );
  if (km == null || km > 2500) return null;
  return km;
};

const partnerLabel = (row) =>
  String(row?.shop_name || row?.mechanic_name || row?.code || 'Partner').trim() || 'Partner';

const whenText = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

async function fetchPartnersByIds(ids) {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  const map = new Map();
  if (!unique.length) return map;
  for (let i = 0; i < unique.length; i += 80) {
    const chunk = unique.slice(i, i + 80);
    const { data } = await serviceRoleClient
      .from('mechanics')
      .select('id, shop_name, mechanic_name, code, service_center_lat, service_center_lng, owner_user_id')
      .in('id', chunk);
    (data || []).forEach((row) => {
      map.set(String(row.id), row);
      if (row.owner_user_id) map.set(String(row.owner_user_id), row);
    });
  }
  const missing = unique.filter((id) => !map.has(id));
  for (let i = 0; i < missing.length; i += 80) {
    const chunk = missing.slice(i, i + 80);
    const { data } = await serviceRoleClient
      .from('mechanics')
      .select('id, shop_name, mechanic_name, code, service_center_lat, service_center_lng, owner_user_id')
      .in('owner_user_id', chunk);
    (data || []).forEach((row) => {
      map.set(String(row.id), row);
      if (row.owner_user_id) map.set(String(row.owner_user_id), row);
    });
  }
  return map;
}

async function fetchJobsByIds(ids) {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  const map = new Map();
  if (!unique.length) return map;
  for (let i = 0; i < unique.length; i += 40) {
    const chunk = unique.slice(i, i + 40);
    const { data, error } = await serviceRoleClient
      .from('service_requests')
      .select(JOB_SELECT)
      .in('id', chunk);
    if (error) throw new Error(error.message);
    (data || []).forEach((row) => map.set(String(row.id), row));
  }
  return map;
}

async function loadRingPage({ mechanicId, offset }) {
  let q = serviceRoleClient
    .from('partner_job_bell_targets')
    .select('mechanic_id, request_id, bell_started_at', { count: 'exact' })
    .order('bell_started_at', { ascending: false })
    .range(offset, offset + PAGE - 1);
  if (mechanicId) q = q.eq('mechanic_id', mechanicId);
  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const rows = data || [];
  const jobs = await fetchJobsByIds(rows.map((r) => r.request_id));
  const partners = await fetchPartnersByIds(rows.map((r) => r.mechanic_id));
  return {
    total: count ?? offset + rows.length,
    items: rows.map((row) => ({
      key: `${row.request_id}-${row.mechanic_id}-${row.bell_started_at}`,
      kind: 'ring',
      at: row.bell_started_at,
      partner: partners.get(String(row.mechanic_id)) || null,
      job: jobs.get(String(row.request_id)) || null,
    })),
  };
}

async function loadCompletedPage({ mechanicId, offset }) {
  let assignIds = [];
  if (mechanicId) {
    const { data: mech } = await serviceRoleClient
      .from('mechanics')
      .select('id, owner_user_id')
      .eq('id', mechanicId)
      .maybeSingle();
    assignIds = [mech?.id, mech?.owner_user_id].filter(Boolean);
  }

  let q = serviceRoleClient
    .from('service_requests')
    .select(JOB_SELECT, { count: 'exact' })
    .in('source', PARTNER_APP_JOB_SOURCES)
    .or(
      'request_status.eq.completed,complaint_status.eq.completed,request_status.eq.closed,complaint_status.eq.closed',
    )
    .not('assigned_mechanic_id', 'is', null)
    .order('updated_at', { ascending: false, nullsFirst: false })
    .range(offset, offset + PAGE - 1);
  if (assignIds.length === 1) q = q.eq('assigned_mechanic_id', assignIds[0]);
  else if (assignIds.length > 1) q = q.in('assigned_mechanic_id', assignIds);

  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const rows = data || [];
  const partners = await fetchPartnersByIds(rows.map((r) => r.assigned_mechanic_id));
  return {
    total: count ?? offset + rows.length,
    items: rows.map((job) => ({
      key: job.id,
      kind: 'completed',
      at: job.updated_at || job.complaint_registered_at || job.requested_at,
      partner: partners.get(String(job.assigned_mechanic_id)) || null,
      job,
    })),
  };
}

export default function PartnerActivityFeed({ open, kind, mechanicId, title, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const offsetRef = useRef(0);
  const doneRef = useRef(false);
  const busyRef = useRef(false);
  const sentinelRef = useRef(null);
  const listRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (!open || busyRef.current || doneRef.current) return;
    busyRef.current = true;
    setLoading(true);
    setError('');
    try {
      const page =
        kind === 'rings'
          ? await loadRingPage({ mechanicId, offset: offsetRef.current })
          : await loadCompletedPage({ mechanicId, offset: offsetRef.current });
      setTotal(page.total);
      setItems((prev) => {
        const seen = new Set(prev.map((row) => row.key));
        return [...prev, ...page.items.filter((row) => !seen.has(row.key))];
      });
      offsetRef.current += page.items.length;
      if (!page.items.length || offsetRef.current >= page.total) doneRef.current = true;
    } catch (e) {
      setError(e.message || 'Could not load details');
      doneRef.current = true;
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  }, [open, kind, mechanicId]);

  useEffect(() => {
    if (!open) return undefined;
    offsetRef.current = 0;
    doneRef.current = false;
    busyRef.current = false;
    setItems([]);
    setTotal(0);
    setError('');
    loadMore();
    return undefined;
  }, [open, kind, mechanicId, loadMore]);

  useEffect(() => {
    if (!open) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root: listRef.current, rootMargin: '160px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [open, loadMore, items.length]);

  if (!open) return null;

  const Icon = kind === 'rings' ? Bell : Briefcase;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-5">
      <button type="button" className="absolute inset-0 bg-slate-900/50" aria-label="Close" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
              <Icon size={13} />
              {kind === 'rings' ? 'Job notifications' : 'Completed cases'}
            </p>
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500">
              {total.toLocaleString('en-IN')} total · scroll to load more
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {items.map((row) => {
            const job = row.job;
            const svc = String(job?.service_type || job?.problem || '').trim() || 'Service not recorded';
            const addr =
              String(job?.pickup_address || job?.location_link || '').trim() || 'No pickup address';
            const km = distanceKm(row.partner, job);
            const status = job?.request_status || job?.complaint_status || '';
            const source = String(job?.source || '').trim();
            return (
              <div key={row.key} className="border-b border-slate-100 py-3">
                <p className="text-sm font-semibold text-slate-900">{svc}</p>
                <p className="mt-0.5 text-xs text-slate-600">{addr}</p>
                {km != null ? (
                  <p className="mt-0.5 text-xs font-semibold text-indigo-700">{km} km from shop</p>
                ) : null}
                <p className="mt-1 text-[11px] text-slate-500">
                  {whenText(row.at)}
                  {row.partner ? ` · ${partnerLabel(row.partner)}` : ''}
                  {job?.assigned_mechanic_name && !row.partner
                    ? ` · ${job.assigned_mechanic_name}`
                    : ''}
                  {status ? ` · ${status}` : ''}
                  {source ? ` · ${source}` : ''}
                </p>
                {job?.customer_name || job?.vehicle_number ? (
                  <p className="text-[11px] text-slate-400">
                    {[job.customer_name, job.vehicle_number].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
              </div>
            );
          })}
          {error ? <p className="py-6 text-center text-sm text-rose-600">{error}</p> : null}
          {!error && !loading && items.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">No records yet</p>
          ) : null}
          <div ref={sentinelRef} className="h-8" />
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export async function countExact(table, apply, selectCol = 'id') {
  let q = serviceRoleClient.from(table).select(selectCol, { count: 'exact', head: true });
  if (apply) q = apply(q);
  const { count, error } = await q;
  if (error) return 0;
  return Number(count || 0);
}
